/**
 * The client's name in the URL: {@code /t/<slug>/...}.
 *
 * <p><b>Pure, and separate from the route that uses it, because the rules matter more than the
 * component does.</b> The slug pattern here is the SAME one {@code RealmIssuers} applies to a realm
 * name on the server — deliberately, because this path is how a browser eventually asks for a
 * particular realm, and a shell that accepted {@code /t/QBE} while the platform rejected the realm
 * {@code QBE} would send somebody to a sign-in page that could never work.
 *
 * <p><b>What this does NOT do yet.</b> It does not choose a Keycloak realm, and it does not brand
 * the sign-in page. Both wait on ADR 0006 step 2, where realms are provisioned. What it does is
 * make the address real: a client following it lands in the platform and signs in, instead of
 * meeting a blank page — which is what {@code /t/qbelocal} did before this existed, because no
 * route matched and React Router rendered nothing at all.
 */

/** What a client slug may be. Identical to the server's realm rule, on purpose. */
const SLUG = /^[a-z0-9][a-z0-9_-]{0,62}$/;

export interface TenantPath {
  slug: string;
  /** Where to continue, always absolute. `/t/qbe` and `/t/qbe/` both continue to `/`. */
  rest: string;
}

/**
 * Reads {@code /t/<slug>/<rest>}, or null when this is not a client-scoped path.
 *
 * <p>Null for a slug that is not a slug, rather than a best guess. An uppercase or
 * punctuation-carrying name reaching this point is a typo or a probe, and both are better served by
 * the ordinary sign-in than by a page pretending to be a client's.
 */
export function readTenantPath(pathname: string): TenantPath | null {
  const match = /^\/t\/([^/]*)(\/.*)?$/.exec(pathname);
  if (!match) {
    return null;
  }
  const slug = decodeURIComponent(match[1]);
  if (!SLUG.test(slug)) {
    return null;
  }
  const rest = match[2] ?? '/';
  return { slug, rest: rest === '' ? '/' : rest };
}

const STORAGE_KEY = 'thesis3.tenantSlug';

/**
 * Remembers which client's address was followed, across the sign-in redirect.
 *
 * <p>Session storage rather than the URL: the OIDC redirect_uri is registered with Keycloak and
 * cannot vary per client without registering 500 of them. The slug goes into the tab's own storage
 * before the redirect and is read back after it, which is the same lifetime the tokens already have.
 *
 * <p>Storage can throw — Safari in private mode, a locked-down profile — and a client whose link
 * merely fails to be remembered should still be able to sign in. So it is best-effort by design.
 */
export function rememberTenant(slug: string): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, slug);
  } catch {
    // Not remembered. Sign-in still works; only the branding would have differed.
  }
  writeTenantCookie(slug);
}

/** The cookie name the Keycloak login theme reads. Changing it changes `brand.js` too. */
const COOKIE = 't3_tenant';

/**
 * Tells the sign-in page which client is arriving.
 *
 * <p><b>A cookie, because nothing else survives the journey.</b> Keycloak renders the login form at
 * a URL it builds itself — `/login-actions/authenticate?session_code=...` — so a parameter added to
 * the original auth request is gone by the time there is a page to brand. Session storage does not
 * cross to the Keycloak origin. A cookie does: cookies ignore port, so one set on `localhost` by
 * the shell on :3000 reaches Keycloak on :8443, and in a real deployment both are behind the same
 * host anyway.
 *
 * <p>`SameSite=Lax` deliberately: the redirect to Keycloak is a top-level GET, which Lax allows,
 * and anything looser would send this on cross-site subrequests for no benefit. It carries no
 * secret — a client's slug is on the address bar of the link they were sent — so `Secure` is not
 * set, or it would never arrive over the plain-HTTP dev origin.
 */
function writeTenantCookie(slug: string): void {
  try {
    document.cookie = `${COOKIE}=${encodeURIComponent(slug)}; path=/; max-age=1800; SameSite=Lax`;
  } catch {
    // The sign-in page keeps the platform's branding. It still signs people in.
  }
}

export function rememberedTenant(): string | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored != null && SLUG.test(stored) ? stored : null;
  } catch {
    return null;
  }
}
